import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import Lottie from "lottie-react";
import PulsingCircle from "../PulsingCircle/PulsingCircle";
import saveButton from "../../../../appAssets/jsons/saveButton.json";
import enableSpeakBtnJson from "../../../../appAssets/jsons/enableSpeakBtn.json";
import closeWhiteJson from "../../../../appAssets/jsons/closeWhite.json";
import shuffleIconVal from "../../../../appAssets/jsons/shuffleIconVal.json";
import crossIcon from "../../../../../assets/Icons/cross.png";
import { JOURNAL_STRINGS } from "../../types";
import "./AudioRecorder.scss";
import LottieWebPlayer from "../../../AppJourneyPage/LottieWebPlayer";

export type AudioRecorderHandle = {
  stopPlayback: () => void;
};

interface AudioRecorderProps {
  /** Triggered with the recorded blob URL when recording stops */
  onRecorded: (blobUrl: string, blob: Blob) => void;
  /** External "stored" audio URL (e.g. from saved answer) */
  storedAudioUri?: string;
  /** When the user already wrote text, hide the speak UI */
  isAnswerTypeText?: boolean;
  /** Show "My idea" / "Suggest me" toggle */
  isShowOwnBtn?: boolean;
  isOwn?: boolean;
  toggleOwnStory?: () => void;
  /** Notify parent we are recording */
  onRecordingChange?: (isRecording: boolean) => void;
  /** When parent disables, stop any playback/recording */
  disableState?: boolean;
  /** Shuffle button (mobile: showShuffleIcon + shuffleBtn) — next question */
  showShuffleBtn?: boolean;
  onShuffle?: () => void;
  /** Save/done callback after playback — fires when user taps the save Lottie */
  onSave?: () => void;
  showSaveBtn?: boolean;
  hideTapButton?: boolean;
}

/**
 * Web equivalent of mobile AudioRecorderPlayer (expo-av) using:
 *   - MediaRecorder for recording
 *   - HTMLAudioElement for playback
 */
const AudioRecorder = forwardRef<AudioRecorderHandle, AudioRecorderProps>(
  (
    {
      onRecorded,
      storedAudioUri,
      isAnswerTypeText,
      isShowOwnBtn = true,
      isOwn = false,
      toggleOwnStory,
      onRecordingChange,
      disableState,
      showShuffleBtn,
      onShuffle,
      onSave,
      showSaveBtn,
      hideTapButton,
    },
    ref,
  ) => {
    const [recording, setRecording] = useState(false);
    const [recordUrl, setRecordUrl] = useState<string>("");
    const [isPlaying, setIsPlaying] = useState(false);

    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    useImperativeHandle(ref, () => ({
      stopPlayback: () => {
        stopSound();
      },
    }));

    useEffect(() => {
      if (storedAudioUri) setRecordUrl(storedAudioUri);
    }, [storedAudioUri]);

    useEffect(() => {
      if (disableState) {
        stopSound();
        setRecordUrl("");
      }
    }, [disableState]);

    useEffect(() => {
      return () => {
        stopSound();
        try {
          mediaRecorderRef.current?.stream
            ?.getTracks()
            .forEach((t) => t.stop());
        } catch {}
      };
    }, []);

    const startRecording = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        const mr = new MediaRecorder(stream);
        chunksRef.current = [];
        mr.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        mr.onstop = () => {
          const blob = new Blob(chunksRef.current, {
            type: mr.mimeType || "audio/webm",
          });
          const url = URL.createObjectURL(blob);
          setRecordUrl(url);
          onRecorded(url, blob);
          stream.getTracks().forEach((t) => t.stop());
        };
        mr.start();
        mediaRecorderRef.current = mr;
        setRecording(true);
        onRecordingChange?.(true);
      } catch (err) {
        console.error("Microphone permission denied:", err);
      }
    };

    const stopRecording = () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") mr.stop();
      mediaRecorderRef.current = null;
      setRecording(false);
      onRecordingChange?.(false);
    };

    const cancelRecording = () => {
      const mr = mediaRecorderRef.current;
      if (mr && mr.state !== "inactive") {
        mr.onstop = null;
        mr.stop();
        mr.stream?.getTracks().forEach((t) => t.stop());
      }
      mediaRecorderRef.current = null;
      chunksRef.current = [];
      setRecording(false);
      onRecordingChange?.(false);
    };

    const clearRecording = () => {
      stopSound();
      setRecordUrl("");
    };

    const playSound = () => {
      if (!recordUrl) return;
      stopSound();
      const audio = new Audio(recordUrl);
      audio.onended = () => setIsPlaying(false);
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
      audioRef.current = audio;
    };

    const stopSound = () => {
      try {
        audioRef.current?.pause();
      } catch {}
      audioRef.current = null;
      setIsPlaying(false);
    };

    if (isAnswerTypeText) return null;

    return (
      <div className="audio-rec">
        <div className="audio-rec__main">
          {!recordUrl && !recording && (
            <button
              type="button"
              className="audio-rec__btn"
              onClick={startRecording}
              disabled={disableState}
              aria-label="Start recording"
            >
              <Lottie
                animationData={enableSpeakBtnJson}
                // animationData={micIconJson}
                loop
                autoplay
                className="audio-rec__lottie"
              />
            </button>
          )}

          {recording && (
            <>
              <PulsingCircle>
                <button
                  type="button"
                  className="audio-rec__btn audio-rec__btn--active"
                  onClick={stopRecording}
                  aria-label="Stop recording"
                >
                  <span className="audio-rec__stop-square" />
                </button>
              </PulsingCircle>
              {/* <button
                type="button"
                className="audio-rec__cancel-btn"
                onClick={cancelRecording}
                aria-label="Cancel recording"
              >
                <img
                  src={crossIcon}
                  alt="cancel"
                  className="audio-rec__cancel-icon"
                />
              </button> */}
            </>
          )}

          {showShuffleBtn && !recordUrl && !recording && (
            <button
              type="button"
              className="audio-rec__shuffle-btn"
              onClick={onShuffle}
              disabled={disableState}
              aria-label="Shuffle prompt"
            >
              <Lottie
                animationData={shuffleIconVal}
                loop={false}
                autoplay={false}
              />
              {/* <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="16 3 21 3 21 8" />
                <line x1="4" y1="20" x2="21" y2="3" />
                <polyline points="21 16 21 21 16 21" />
                <line x1="15" y1="15" x2="21" y2="21" />
                <line x1="4" y1="4" x2="9" y2="9" />
              </svg> */}
            </button>
          )}

          {!hideTapButton && isShowOwnBtn && !recordUrl && !recording && (
            <button
              type="button"
              className="audio-rec__own-btn"
              onClick={() => toggleOwnStory?.()}
            >
              {!isOwn ? JOURNAL_STRINGS.giveIdea : JOURNAL_STRINGS.ownIdea}
            </button>
          )}
        </div>

        {recordUrl && (
          <div className="audio-rec__playback">
            <button
              type="button"
              className="audio-rec__play-btn"
              onClick={isPlaying ? stopSound : playSound}
            >
              {isPlaying ? (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <rect x="6" y="5" width="4" height="14" rx="1" />
                  <rect x="14" y="5" width="4" height="14" rx="1" />
                </svg>
              ) : (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon
                    points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"
                    fill="currentColor"
                    stroke="none"
                  />
                  <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                  <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
              )}
              {isPlaying ? "Stop Audio" : "Play Audio"}
            </button>
            {/* {showSaveBtn && (
              <button
                type="button"
                className="audio-rec__save-btn"
                onClick={() => {
                  stopSound();
                  onSave?.();
                }}
                aria-label="Save recording"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )} */}
            <div
              onClick={() => {
                stopSound();
                onSave?.();
              }}
              // onClick={clearRecording}
            >
              <Lottie
                animationData={saveButton}
                loop={false}
                autoplay={false}
                className="audio-rec__cancel-btn"
              />
            </div>
            <div className="audio-rec__btn_cancel" onClick={clearRecording}>
              <LottieWebPlayer
                source={closeWhiteJson}
                style={{ width: 32, height: 32 }}
                autoPlay
                loop={false}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

AudioRecorder.displayName = "AudioRecorder";
export default AudioRecorder;
